import { Button, Modal } from "react-bootstrap";

const OPRExplainModal = ({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) => (
  <Modal show={show} onHide={onHide} centered size="lg" scrollable>
    <Modal.Header closeButton>
      <Modal.Title>Operating PR (OPR)</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Same metered generation in the numerator as GPR, but the
        denominator is the InRange PV model&apos;s expected generation
        given the actual observed weather &mdash; the same model used for
        the forecast, applied to actual irradiance instead of TMY.
      </p>
      <p>
        <strong>OPR = &Sigma; E_prod / &Sigma; E_expected</strong>
      </p>
      <p>
        E_expected runs Meteonorm GTI through the full loss-factor pipeline
        (temperature, inverter clipping, soiling, wiring, availability,
        &hellip;) excluding only:
      </p>
      <ul>
        <li>
          <strong>GICP</strong> &mdash; already represented in the
          orientation-specific GTI.
        </li>
        <li>
          <strong>YoY variability</strong> &mdash; a probabilistic forecast
          adjustment that doesn&apos;t apply to a single observed period.
        </li>
      </ul>
      <p>
        Panel degradation is folded in via the same loss-factor pipeline.
      </p>
      <p>
        Because the denominator already accounts for expected operational
        losses, a healthy system should sit close to 100% rather than near
        the Designed PR. OPR uses{" "}
        <strong>fixed global thresholds</strong> rather than a per-site
        contract:
      </p>
      <ul>
        <li>
          <strong>Green</strong> at 90% or above.
        </li>
        <li>
          <strong>Amber</strong> between 85% and 90%.
        </li>
        <li>
          <strong>Red</strong> below 85%.
        </li>
      </ul>
      <p>
        OPR drifting downward while GPR stays green is the typical signal
        of a creeping operational issue (soiling, partial inverter outage,
        miscalibrated sensors) that hasn&apos;t yet eaten through the
        contract&apos;s loss-factor budget.
      </p>
      <p>
        <strong>Curtailment handling.</strong> Sites with a configured export
        limit are automatically checked for curtailment. A period is flagged
        as curtailed when metered export hits the limit (or, for zero-export
        sites, when import drops unusually low during generation). Two OPR
        variants are reported, differing only in how those flagged periods are
        treated:
      </p>
      <ul>
        <li>
          <strong>OPR (excluded)</strong> &mdash; flagged periods are removed
          from both numerator and denominator, so OPR is measured only over
          the periods the plant was free to run.
        </li>
        <li>
          <strong>OPR (deemed)</strong> &mdash; monitored only. Flagged
          periods keep their expected generation in the denominator, and the
          modelled lost generation is credited back into the numerator, so OPR
          reflects what the plant <em>would</em> have produced absent
          curtailment. A flagged period with no model estimate earns no credit
          but still sits in the denominator, so a deemed OPR can read slightly
          below the excluded one.
        </li>
      </ul>
      <p className="text-muted small mb-0">
        &ldquo;Last month&rdquo; covers the previous complete calendar
        month. &ldquo;Last 12 months&rdquo; covers the trailing 12 complete
        calendar months. Offtaker-only sites (no on-site PV) do not show
        an OPR.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

export default OPRExplainModal;
